/**
 * Авторизация.
 *
 * Два сценария входа:
 *   • приложение открыто внутри Telegram — входим молча по подписи,
 *     человек ничего не нажимает;
 *   • обычный браузер — сначала пробуем восстановить сессию по куке,
 *     и только если не вышло, показываем экран входа.
 *
 * Права приходят с сервера списком. Клиент их только показывает или
 * прячет кнопки — настоящая проверка всё равно на бэкенде.
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, setToken } from './api';
import type { Me, User } from './types';

type TelegramWebApp = {
  initData: string;
  ready: () => void;
  expand: () => void;
  colorScheme: string;
};

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

type AuthState = {
  user: User | null;
  permissions: Set<string>;
  settings: Me['settings'] | null;
  loading: boolean;
  error: string | null;
  /** Вход выполнен временным паролем — интерфейс потребует его сменить */
  mustChangePassword: boolean;
  can: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
  loginWithPassword: (username: string, password: string, totp?: string) => Promise<void>;
  /** Вход для разработки: без Telegram и пароля. Работает только локально. */
  loginDev: () => Promise<void>;
  logout: () => Promise<void>;
  reload: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [permissions, setPermissions] = useState<Set<string>>(new Set());
  const [settings, setSettings] = useState<Me['settings'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mustChangePassword, setMustChange] = useState(false);

  const loadMe = async () => {
    const me = await api.get<Me>('/api/auth/me');
    setUser(me.user);
    setPermissions(new Set(me.permissions));
    setSettings(me.settings);
    setMustChange(Boolean(me.mustChangePassword));
  };

  useEffect(() => {
    void (async () => {
      const tg = window.Telegram?.WebApp;

      // Открыто внутри Telegram — вход происходит сам
      if (tg?.initData) {
        tg.ready();
        tg.expand();
        try {
          const res = await api.post<{ accessToken: string }>('/api/auth/telegram', {
            initData: tg.initData,
          });
          setToken(res.accessToken);
          await loadMe();
          setLoading(false);
          return;
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Не удалось войти через Telegram');
          setLoading(false);
          return;
        }
      }

      // Обычный браузер — пробуем восстановить сессию
      if (await api.refresh()) {
        try {
          await loadMe();
        } catch {
          setToken(null);
        }
      }
      setLoading(false);
    })();
  }, []);

  const value: AuthState = {
    user,
    permissions,
    settings,
    loading,
    error,
    mustChangePassword,
    can: (permission) => permissions.has(permission),
    hasRole: (role) => Boolean(user?.roles.includes(role as never)),

    loginWithPassword: async (username, password, totp) => {
      const res = await api.post<{ accessToken: string }>('/api/auth/password', {
        username,
        password,
        totp,
      });
      setToken(res.accessToken);
      await loadMe();
    },

    loginDev: async () => {
      const res = await api.post<{ accessToken: string }>('/api/auth/dev');
      setToken(res.accessToken);
      await loadMe();
    },

    logout: async () => {
      await api.post('/api/auth/logout').catch(() => {});
      setToken(null);
      setUser(null);
      setPermissions(new Set());
      setMustChange(false);
    },

    reload: loadMe,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth вызван вне AuthProvider');
  return ctx;
}
